import {
  adminApiClient,
  extractDownloadFilename,
  resolveBlobApiErrorMessage,
} from '@/services/admin/baseService'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import {
  buildPaginatedResponse,
  extractArrayFromApiPayload,
} from '@/services/admin/paginatedResponseHelpers'
import { toBooleanValue } from '@/services/admin/valueHelpers'
import type {
  ApiResponse,
  ContractDeploymentListParams,
  ContractDeploymentRecord,
  ContractDeploymentTerminalCleanupEvidence,
  ContractInitializationQueryParams,
  ContractInitializationStatus,
  ContractRegistryListParams,
  ContractRegistryRecord,
  ContractRegistryStatusUpdate,
  OrderLifecycleSweepExtendNote,
  PaginatedResponse,
} from '@/types'

export interface ContractPreparationTriggerResult {
  ordersScanned: number
  ordersQueued: number
  ordersSkipped: number
  alreadyQueued: number
  failed: number
  message?: string
  items: LifecycleBatchItemResult[]
}

export interface LifecycleBatchItemResult {
  inputId: string
  resourceId?: string
  status: 'queued' | 'already_queued' | 'skipped' | 'failed'
  reason?: string
  message?: string
}

export interface LifecycleBatchTriggerResult {
  total: number
  queued: number
  alreadyQueued: number
  skipped: number
  failed: number
  items: LifecycleBatchItemResult[]
}

const normalizeLifecycleBatchItems = (value: unknown): LifecycleBatchItemResult[] => {
  if (!Array.isArray(value)) {
    throw new Error('Invalid lifecycle batch item results')
  }

  return value.map((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      throw new Error(`Invalid lifecycle batch item result at index ${index}`)
    }
    const record = item as Record<string, unknown>
    const inputId = typeof record.inputId === 'string' ? record.inputId : ''
    const status = record.status
    if (
      !inputId
      || (status !== 'queued'
        && status !== 'already_queued'
        && status !== 'skipped'
        && status !== 'failed')
    ) {
      throw new Error(`Invalid lifecycle batch item result at index ${index}`)
    }

    for (const field of ['resourceId', 'reason', 'message'] as const) {
      if (record[field] !== undefined && typeof record[field] !== 'string') {
        throw new Error(`Invalid lifecycle batch item result at index ${index}`)
      }
    }

    return {
      inputId,
      resourceId: typeof record.resourceId === 'string' ? record.resourceId : undefined,
      status,
      reason: typeof record.reason === 'string' ? record.reason : undefined,
      message: typeof record.message === 'string' ? record.message : undefined,
    }
  })
}

const normalizeLifecycleBatchTriggerResult = (
  value: unknown
): LifecycleBatchTriggerResult => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid lifecycle batch result')
  }

  const record = value as Record<string, unknown>
  const items = normalizeLifecycleBatchItems(record.items)
  const requireCount = (field: keyof Omit<LifecycleBatchTriggerResult, 'items'>): number => {
    const count = record[field]
    if (typeof count !== 'number' || !Number.isSafeInteger(count) || count < 0) {
      throw new Error(`Invalid lifecycle batch ${field} count`)
    }
    return count
  }
  const result: LifecycleBatchTriggerResult = {
    total: requireCount('total'),
    queued: requireCount('queued'),
    alreadyQueued: requireCount('alreadyQueued'),
    skipped: requireCount('skipped'),
    failed: requireCount('failed'),
    items,
  }
  const actual = items.reduce(
    (summary, item) => {
      if (item.status === 'queued') summary.queued++
      if (item.status === 'already_queued') summary.alreadyQueued++
      if (item.status === 'skipped') summary.skipped++
      if (item.status === 'failed') summary.failed++
      return summary
    },
    { queued: 0, alreadyQueued: 0, skipped: 0, failed: 0 }
  )

  if (
    result.total !== items.length
    || result.queued !== actual.queued
    || result.alreadyQueued !== actual.alreadyQueued
    || result.skipped !== actual.skipped
    || result.failed !== actual.failed
  ) {
    throw new Error('Lifecycle batch aggregate counts do not match item results')
  }

  return result
}

const normalizePaginatedResponse = <T>(
  payload: ApiResponse<any> | undefined,
  options: { keys?: string[]; defaultPage?: number; defaultLimit?: number } = {}
): PaginatedResponse<T> => {
  const payloadAny = payload as any
  const items = extractArrayFromApiPayload<T>(payloadAny, {
    keys: options.keys,
    candidateKeys: ['items', 'records', 'deployments', 'users', 'merchants', 'orders', 'logs', 'refunds'],
    preferExplicitKeys: true,
  })
  return buildPaginatedResponse(payload, items, options)
}

const toStringSafe = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`
  }
  return undefined
}

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const ensureJsonString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }
  try {
    return JSON.stringify(value)
  } catch {
    return undefined
  }
}

const parseSweepExtendNotes = (payload: string): OrderLifecycleSweepExtendNote[] => {
  if (!payload) {
    return []
  }

  try {
    const parsed = JSON.parse(payload)
    if (Array.isArray(parsed)) {
      return parsed as OrderLifecycleSweepExtendNote[]
    }
  } catch {
    // Ignore parsing errors and fall back to empty array
  }
  return []
}

const normalizeRegistryStatusId = (status: unknown): ContractRegistryRecord['status_id'] => {
  const raw = toStringSafe(status)
  if (!raw) {
    return 'orrs_active'
  }
  const normalized = raw.trim()
  if (normalized.startsWith('orrs_')) {
    switch (normalized as ContractRegistryRecord['status_id']) {
      case 'orrs_active':
      case 'orrs_on_hold':
      case 'orrs_deployed':
      case 'orrs_cancelled':
        return normalized as ContractRegistryRecord['status_id']
      default:
        break
    }
  }
  switch (normalized.toLowerCase()) {
    case 'active':
      return 'orrs_active'
    case 'on_hold':
    case 'onhold':
    case 'hold':
      return 'orrs_on_hold'
    case 'deployed':
      return 'orrs_deployed'
    case 'cancelled':
    case 'canceled':
      return 'orrs_cancelled'
    default:
      return 'orrs_active'
  }
}

const mapContractRegistryRecord = (input: unknown): ContractRegistryRecord => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return {
      id: '',
      order_id: '',
      merchant_id: '',
      chain_id: 'unknown',
      token_address: '',
      amount: '0',
      amount_in_smallest_unit: '0',
      predicted_contract_address: '',
      status_id: 'orrs_active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      orderId: '',
      merchant_name: 'Unknown Merchant',
      chain_code: undefined,
      chain_name: undefined,
      chain_display_name: undefined,
      token_symbol: undefined,
      formatted_amount: undefined,
      formatted_amount_value: undefined,
      status_name: undefined,
      status: 'orrs_active',
      deployment_tx_hash: undefined,
      deployment_block_number: undefined,
      deployment_gas_used: undefined,
      deployment_cost_usd: undefined,
      error_message: undefined,
      user_payments: undefined,
      user_payments_count: undefined,
      payments_last_sync: undefined,
      deployed_at: undefined,
      address_verification_status_id: 'avs_pending',
    }
  }
  const record = input as Record<string, unknown>

  const id = toStringSafe(record.id) ?? toStringSafe(record.registry_id) ?? ''
  const orderInternalId = toStringSafe(record.order_id) ?? toStringSafe(record.orderId) ?? ''
  const orderBusinessId =
    toStringSafe(record.order_business_id) ??
    toStringSafe(record.orderBusinessId) ??
    orderInternalId
  const merchantBusinessId =
    toStringSafe(record.merchant_business_id) ?? toStringSafe(record.merchantBusinessId)
  const merchantId =
    toStringSafe(record.merchant_id) ?? merchantBusinessId ?? toStringSafe(record.merchantId) ?? ''
  const merchantName =
    toStringSafe(record.merchant_name) ??
    toStringSafe(record.merchantName) ??
    merchantId ??
    'Unknown Merchant'
  const chainCode = toStringSafe(record.chain_code) ?? toStringSafe(record.chainCode)

  const chainCandidates = [
    chainCode,
    record.chain_id,
    record.registryChainId,
    record.chainId,
    record.chain_identifier,
    record.numeric_chain_id,
  ]
  let resolvedChainId: string | undefined
  for (const candidate of chainCandidates) {
    if (candidate === null || candidate === undefined) continue
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      resolvedChainId = `${candidate}`
      break
    }
    const candidateString = toStringSafe(candidate)
    if (candidateString) {
      resolvedChainId = candidateString
      break
    }
  }
  const chainId = resolvedChainId ?? chainCode ?? 'unknown'

  const chainName = toStringSafe(record.chain_name) ?? toStringSafe(record.chainName)
  const chainDisplayName =
    toStringSafe(record.chain_display_name) ??
    toStringSafe(record.chainDisplayName) ??
    chainName ??
    (chainCode ?? chainId)
  const chainCompatibility =
    toStringSafe(record.chain_compatibility) ?? toStringSafe(record.chainCompatibility)
  const tokenAddress =
    toStringSafe(record.token_address) ?? toStringSafe(record.tokenAddress) ?? ''
  const paymentAddress =
    toStringSafe(record.payment_address) ?? toStringSafe(record.paymentAddress)
  const paymentTokenAddress =
    toStringSafe(record.payment_token_address) ?? toStringSafe(record.paymentTokenAddress)
  const programId = toStringSafe(record.program_id) ?? toStringSafe(record.programId)
  const tokenSymbol = toStringSafe(record.token_symbol) ?? toStringSafe(record.tokenSymbol)
  const formattedAmount = toStringSafe(record.formatted_amount) ?? toStringSafe(record.formattedAmount)
  const formattedAmountValueRaw =
    record.formatted_amount_value ?? record.formattedAmountValue
  const formattedAmountValue =
    typeof formattedAmountValueRaw === 'number' && Number.isFinite(formattedAmountValueRaw)
      ? formattedAmountValueRaw
      : undefined
  const amountSmallest =
    toStringSafe(record.order_total_amount_in_smallest_unit) ??
    toStringSafe(record.orderTotalAmountInSmallestUnit) ??
    toStringSafe(record.amount_in_smallest_unit) ??
    toStringSafe(record.amountInSmallestUnit) ??
    toStringSafe(record.order_total_amount) ??
    toStringSafe(record.orderTotalAmount) ??
    '0'
  const amount = toStringSafe(record.amount) ?? (formattedAmountValue !== undefined
    ? formattedAmountValue.toString()
    : amountSmallest)
  const statusId = normalizeRegistryStatusId(record.status_id ?? record.status ?? record.statusId)
  const statusName = toStringSafe(record.status_name) ?? toStringSafe(record.statusName)
  const predictedAddress =
    toStringSafe(record.predicted_contract_address) ??
    toStringSafe(record.predictedContractAddress) ??
    ''
  const userPaymentsSource = record.user_payments ?? record.userPayments
  const userPayments =
    typeof userPaymentsSource === 'string'
      ? userPaymentsSource
      : ensureJsonString(userPaymentsSource)
  const userPaymentsCount =
    toNumberOrUndefined(record.user_payments_count ?? record.userPaymentsCount)
  const paymentsLastSync =
    toStringSafe(record.payments_last_sync) ??
    toStringSafe(record.paymentsLastSync) ??
    toStringSafe(record.payments_last_synced_at) ??
    toStringSafe(record.paymentsSyncedAt)
  const deploymentTxHash =
    toStringSafe(record.deployment_tx_hash) ??
    toStringSafe(record.deploymentTxHash) ??
    toStringSafe(record.last_deployment_tx_hash) ??
    toStringSafe(record.lastDeploymentTxHash)
  const deploymentBlockNumber =
    toNumberOrUndefined(record.deployment_block_number ?? record.deploymentBlockNumber)
  const deploymentGasUsed =
    toStringSafe(record.deployment_gas_used) ?? toStringSafe(record.deploymentGasUsed)
  const deploymentCostUsd =
    toStringSafe(record.deployment_cost_usd) ?? toStringSafe(record.deploymentCostUsd)
  const errorMessage = toStringSafe(record.error_message) ?? toStringSafe(record.errorMessage)
  const createdAt = toStringSafe(record.created_at) ?? toStringSafe(record.createdAt) ?? new Date().toISOString()
  const updatedAt = toStringSafe(record.updated_at) ?? toStringSafe(record.updatedAt) ?? createdAt
  const deployedAt =
    toStringSafe(record.deployed_at) ??
    toStringSafe(record.deployedAt) ??
    toStringSafe(record.last_deployment_at) ??
    toStringSafe(record.lastDeploymentAt)
  const addressVerificationStatusId =
    toStringSafe(record.address_verification_status_id) ??
    toStringSafe(record.addressVerificationStatusId) ??
    'avs_pending'

  const rawRegistryChainId = toStringSafe(record.chain_id) ?? undefined
  const deployTriggerLogJson =
    ensureJsonString(record.deploy_trigger_log_json ?? record.deployTriggerLogJson) ??
    undefined

  const normalized: ContractRegistryRecord = {
    ...(record as Record<string, unknown>),
    id,
    order_id: orderInternalId,
    orderId: orderBusinessId ?? orderInternalId,
    merchant_id: merchantId,
    merchant_name: merchantName,
    chain_id: chainId,
    chain_code: chainCode,
    chain_compatibility: chainCompatibility,
    chain_name: chainName,
    chain_display_name: chainDisplayName,
    token_address: tokenAddress,
    payment_address: paymentAddress,
    payment_token_address: paymentTokenAddress,
    program_id: programId,
    token_symbol: tokenSymbol,
    amount: amount ?? '0',
    amount_in_smallest_unit: amountSmallest ?? '0',
    formatted_amount: formattedAmount,
    formatted_amount_value: formattedAmountValue,
    predicted_contract_address: predictedAddress,
    status_id: statusId,
    status_name: statusName,
    status: statusId,
    deployment_tx_hash: deploymentTxHash,
    deployment_block_number: deploymentBlockNumber,
    deployment_gas_used: deploymentGasUsed,
    deployment_cost_usd: deploymentCostUsd,
    error_message: errorMessage,
    user_payments: userPayments,
    user_payments_count: userPaymentsCount,
    payments_last_sync: paymentsLastSync,
    deploy_trigger_log_json: deployTriggerLogJson ?? null,
    created_at: createdAt,
    updated_at: updatedAt,
    deployed_at: deployedAt,
    address_verification_status_id: addressVerificationStatusId,
  }

  if (merchantBusinessId) {
    (normalized as any).merchant_business_id = merchantBusinessId
  }

  if (rawRegistryChainId) {
    (normalized as any).registry_chain_id = rawRegistryChainId
  }

  return normalized
}

const normalizeVerificationStatusId = (status: unknown): ContractDeploymentRecord['address_verification_status_id'] => {
  const raw = toStringSafe(status)
  if (!raw) {
    return 'avs_pending'
  }
  const normalized = raw.trim()
  if (normalized.startsWith('avs_')) {
    if (normalized === 'avs_pending' || normalized === 'avs_verified' || normalized === 'avs_failed') {
      return normalized as ContractDeploymentRecord['address_verification_status_id']
    }
  }
  switch (normalized.toLowerCase()) {
    case 'verified':
    case 'success':
    case 'matched':
      return 'avs_verified'
    case 'failed':
    case 'mismatch':
    case 'error':
      return 'avs_failed'
    case 'pending':
    case 'queued':
    case 'processing':
    default:
      return 'avs_pending'
  }
}

const normalizeDeploymentStatusId = (status: unknown): string => {
  const raw = toStringSafe(status)
  if (!raw) {
    return 'cds_pending'
  }
  const normalized = raw.trim()
  if (normalized.startsWith('cds_')) {
    return normalized
  }
  switch (normalized.toLowerCase()) {
    case 'successful':
    case 'success':
    case 'completed':
      return 'cds_successful'
    case 'failed':
    case 'fail':
    case 'error':
      return 'cds_failed'
    case 'reverted':
    case 'rolled_back':
      return 'cds_reverted'
    case 'pending':
    case 'queued':
    case 'deploying':
    default:
      return `cds_${normalized.replace(/\s+/g, '_')}`
  }
}

const normalizeDeploymentMethod = (
  method: unknown
): ContractDeploymentRecord['deployment_method'] => {
  const raw = toStringSafe(method)
  if (!raw) {
    return undefined
  }

  const normalized = raw.trim().toLowerCase()
  if (normalized === 'normal') {
    return 'normal'
  }

  if (normalized === 'recovered_from_chain' || normalized === 'recovered-from-chain' || normalized === 'recovered') {
    return 'recovered_from_chain'
  }

  return undefined
}

const normalizeTerminalCleanupEvidence = (
  value: unknown
): ContractDeploymentTerminalCleanupEvidence | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined
  }

  const evidence = value as Record<string, unknown>
  const recordId = toStringSafe(evidence.recordId)
  if (!recordId) {
    return undefined
  }

  return {
    recordId,
    source: toStringSafe(evidence.source) ?? 'unknown',
    statusId: toStringSafe(evidence.statusId) ?? 'unknown',
    verificationStatus: toStringSafe(evidence.verificationStatus) ?? 'unknown',
    orderClosed: toBooleanValue(evidence.orderClosed, false),
    signature: toStringSafe(evidence.signature),
    verifiedAt: toStringSafe(evidence.verifiedAt),
    updatedAt: toStringSafe(evidence.updatedAt) ?? '',
  }
}

const mapContractDeploymentRecord = (record: any): ContractDeploymentRecord => {
  if (!record || typeof record !== 'object') {
    const now = new Date().toISOString()
    return {
      id: '',
      order_id: '',
      orderId: '',
      registry_id: '',
      deployment_attempt: 1,
      chain_id: 'unknown',
      predicted_contract_address: '',
      address_verification_status_id: 'avs_pending',
      status_id: 'cds_pending',
      created_at: now,
      updated_at: now,
    }
  }

  const orderInfo = (record.order ?? record.orderDetails?.order) as Record<string, unknown> | undefined

  const orderBusinessId =
    toStringSafe(orderInfo?.orderId) ??
    toStringSafe(record.orderId) ??
    toStringSafe(record.order_id) ??
    ''

  const internalOrderId =
    toStringSafe(record.order_id) ??
    toStringSafe((record as any).orderIdInternal) ??
    orderBusinessId

  const registryId = toStringSafe(record.registryId) ?? toStringSafe(record.registry_id) ?? ''

  const chainCandidates = [
    record.chain_numeric_id,
    record.chainNumericId,
    record.registryChainId,
    record.registry_chain_id,
    record.chain_identifier,
    record.chainId,
    record.chain_id,
    (orderInfo as any)?.numericChainId,
    orderInfo?.chainId,
    (orderInfo as any)?.chain_id,
    record.chainCode ?? record.chain_code,
    (orderInfo as any)?.chainCode,
  ]
  let resolvedChainId: string | undefined
  for (const candidate of chainCandidates) {
    if (candidate === null || candidate === undefined) continue
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      resolvedChainId = `${candidate}`
      break
    }
    const candidateString = toStringSafe(candidate)
    if (candidateString) {
      resolvedChainId = candidateString
      break
    }
  }
  const chainDisplayNameRaw =
    toStringSafe(record.chain_display_name) ?? toStringSafe(record.chainDisplayName)

  const chainName =
    chainDisplayNameRaw ??
    toStringSafe(record.chainName) ??
    toStringSafe(record.chain_name) ??
    toStringSafe(orderInfo?.chainName) ??
    undefined

  const deploymentAttempt =
    toNumberOrUndefined(record.deploymentAttempt ?? record.deployment_attempt) ?? 1

  const predictedContractAddress =
    toStringSafe(record.predictedContractAddress) ??
    toStringSafe(record.predicted_contract_address) ??
    ''

  const deployedContractAddress =
    toStringSafe(record.deployedContractAddress) ??
    toStringSafe(record.deployed_contract_address) ??
    undefined

  const paymentAddress =
    toStringSafe(record.paymentAddress) ??
    toStringSafe(record.payment_address) ??
    undefined

  const operationalAddress =
    toStringSafe(record.operationalAddress) ??
    toStringSafe(record.operational_address) ??
    undefined

  const operationalAddressTypeRaw =
    toStringSafe(record.operationalAddressType) ??
    toStringSafe(record.operational_address_type)
  const operationalAddressType = operationalAddressTypeRaw === 'vault_token_account'
    ? 'vault_token_account'
    : 'contract'

  const chainCompatibility =
    toStringSafe(record.chainCompatibility) ??
    toStringSafe(record.chain_compatibility) ??
    undefined

  const deploymentTxHash =
    toStringSafe(record.deploymentTxHash) ??
    toStringSafe(record.deployment_tx_hash) ??
    undefined

  const deploymentBlockNumber = toNumberOrUndefined(
    record.deploymentBlockNumber ?? record.deployment_block_number
  )

  const deployedAt =
    toStringSafe(record.deployedAt) ??
    toStringSafe(record.deployed_at) ??
    undefined

  const gasUsed =
    toStringSafe(record.gasUsed) ??
    toStringSafe(record.gas_used) ??
    undefined

  const gasPrice =
    toStringSafe(record.gasPrice) ??
    toStringSafe(record.gas_price) ??
    undefined

  const deploymentCostSource = record.deploymentCostUsd ?? record.deployment_cost_usd
  let deploymentCostUsd: number | undefined
  if (typeof deploymentCostSource === 'number' && Number.isFinite(deploymentCostSource)) {
    deploymentCostUsd = deploymentCostSource
  } else if (typeof deploymentCostSource === 'string') {
    const parsedCost = Number(deploymentCostSource)
    if (Number.isFinite(parsedCost)) {
      deploymentCostUsd = parsedCost
    }
  }

  const tokenAddress =
    toStringSafe(record.tokenAddress) ??
    toStringSafe(record.token_address) ??
    undefined

  const tokenSymbol =
    toStringSafe(record.tokenSymbol) ??
    toStringSafe(orderInfo && (orderInfo as any).tokenSymbol) ??
    undefined

  const tokenDecimals = toNumberOrUndefined(
    record.tokenDecimals ?? record.token_decimals ?? (orderInfo as any)?.tokenDecimals
  )

  const verificationNotes =
    toStringSafe(record.verificationNotes) ??
    toStringSafe(record.verification_notes) ??
    undefined

  const deployNotes =
    toStringSafe(record.deployNotes) ??
    toStringSafe(record.deploy_notes) ??
    undefined

  const initializationNotes =
    toStringSafe(record.initializationNotes) ??
    toStringSafe(record.initialization_notes) ??
    undefined

  const isPassSettlementPreviewSource =
    record.isPassSettlementPreview ?? record.is_pass_settlement_preview
  const isPassSettlementPreview =
    isPassSettlementPreviewSource === null || isPassSettlementPreviewSource === undefined
      ? undefined
      : toBooleanValue(isPassSettlementPreviewSource, false)

  const settlementPreviewNotes =
    toStringSafe(record.settlementPreviewNotes) ??
    toStringSafe(record.settlement_preview_notes) ??
    undefined

  const terminalCleanupState = (() => {
    const raw =
      toStringSafe(record.terminalCleanupState) ??
      toStringSafe(record.terminal_cleanup_state) ??
      undefined
    if (
      raw === 'expired_cleanup_completed' ||
      raw === 'refund_cleanup_completed' ||
      raw === 'manual_close_cleanup_completed'
    ) {
      return raw
    }
    return undefined
  })()

  const terminalCleanupRecordId =
    toStringSafe(record.terminalCleanupRecordId) ??
    toStringSafe(record.terminal_cleanup_record_id) ??
    undefined

  const orderStatusId =
    toStringSafe(record.orderStatusId) ??
    toStringSafe(record.order_status_id) ??
    undefined

  const terminalCleanupEvidence = normalizeTerminalCleanupEvidence(
    record.terminalCleanupEvidence
  )

  const sweepOpenAt =
    toStringSafe(record.sweepOpenAt) ??
    toStringSafe(record.sweep_open_at) ??
    undefined

  const sweepExtendMaxAt =
    toStringSafe(record.sweepExtendMaxAt) ??
    toStringSafe(record.sweep_extend_max_at) ??
    undefined

  const sweepExtendNotesRaw =
    toStringSafe(record.sweepExtendNotes) ??
    toStringSafe(record.sweep_extend_notes) ??
    undefined
  const sweepExtendNotes = sweepExtendNotesRaw
    ? parseSweepExtendNotes(sweepExtendNotesRaw)
    : []

  const sweepExtendTxHash =
    toStringSafe(record.sweepExtendTxHash) ??
    toStringSafe(record.sweep_extend_tx_hash) ??
    undefined

  const sweepExtendPreviousUnlockAt =
    toStringSafe(record.sweepExtendPreviousUnlockAt) ??
    toStringSafe(record.sweep_extend_previous_unlock_at) ??
    undefined

  const sweepExtendPeriod = toNumberOrUndefined(
    record.sweepExtendPeriod ?? record.sweep_extend_period
  )

  const sweepExtendedCountsRaw = record.sweepExtendedCounts ?? record.sweep_extended_counts
  const sweepExtendedCounts =
    typeof sweepExtendedCountsRaw === 'number'
      ? sweepExtendedCountsRaw
      : sweepExtendedCountsRaw !== undefined
        ? Number(sweepExtendedCountsRaw)
        : undefined

  const statusName =
    toStringSafe(record.statusName) ??
    toStringSafe(record.status_name) ??
    undefined

  const statusDescription =
    toStringSafe(record.statusDescription) ??
    toStringSafe(record.status_description) ??
    undefined

  const merchantName =
    toStringSafe(record.merchantName) ??
    toStringSafe(record.merchant_name) ??
    undefined

  const createdAt = toStringSafe(record.createdAt) ?? toStringSafe(record.created_at) ?? new Date().toISOString()
  const updatedAt = toStringSafe(record.updatedAt) ?? toStringSafe(record.updated_at) ?? createdAt

  const normalizedChainId = resolvedChainId ?? 'unknown'

  const normalizedRecord: ContractDeploymentRecord = {
    ...(record as Record<string, unknown>),
    id: toStringSafe(record.id) ?? '',
    order_id: internalOrderId || orderBusinessId || '',
    orderId: orderBusinessId || internalOrderId || '',
    registry_id: registryId || '',
    deployment_attempt: deploymentAttempt,
    chain_id: normalizedChainId,
    chain_name: chainName,
    predicted_contract_address: predictedContractAddress,
    deployed_contract_address: deployedContractAddress,
    payment_address: paymentAddress,
    operational_address: operationalAddress,
    operational_address_type: operationalAddressType,
    address_mismatch: toBooleanValue(
      record.addressMismatch ?? record.address_mismatch,
      false
    ),
    chain_compatibility: chainCompatibility,
    deployment_tx_hash: deploymentTxHash,
    deployment_block_number: deploymentBlockNumber,
    deployed_at: deployedAt,
    gas_used: gasUsed,
    gas_price: gasPrice,
    deployment_cost_usd: deploymentCostUsd,
    token_address: tokenAddress,
    token_symbol: tokenSymbol,
    token_decimals: tokenDecimals,
    address_verification_status_id: normalizeVerificationStatusId(
      record.address_verification_status_id ?? record.verificationStatus
    ),
    verification_notes: verificationNotes,
    deploy_notes: deployNotes,
    is_initialized: toBooleanValue(record.is_initialized ?? record.isInitialized, false),
    initialization_notes: initializationNotes,
    is_pass_settlement_preview: isPassSettlementPreview,
    settlement_preview_notes: settlementPreviewNotes,
    terminal_cleanup_state: terminalCleanupState,
    terminal_cleanup_record_id: terminalCleanupRecordId,
    terminalCleanupState,
    terminalCleanupRecordId,
    orderStatusId,
    terminalCleanupEvidence,
    status_id: normalizeDeploymentStatusId(record.status_id ?? record.statusId),
    sweep_open_at: sweepOpenAt,
    sweep_extend_max_at: sweepExtendMaxAt,
    sweep_extend_notes: sweepExtendNotesRaw,
    sweep_extend_tx_hash: sweepExtendTxHash,
    sweep_extend_previous_unlock_at: sweepExtendPreviousUnlockAt,
    sweep_extend_period: sweepExtendPeriod,
    sweep_extended_counts: sweepExtendedCounts ?? 0,
    sweepExtendMaxAt,
    sweepExtendNotes,
    sweepExtendTxHash,
    sweepExtendPreviousUnlockAt,
    sweepExtendPeriod: sweepExtendPeriod,
    sweepExtendedCounts: sweepExtendedCounts ?? 0,
    status_name: statusName,
    status_description: statusDescription,
    deployment_method: normalizeDeploymentMethod(record.deployment_method ?? record.deploymentMethod),
    recovery_notes: toStringSafe(record.recoveryNotes) ?? undefined,
    merchant_name: merchantName,
    created_at: createdAt,
    updated_at: updatedAt,
  }

  if (orderInfo && typeof orderInfo === 'object') {
    (normalizedRecord as any).order = orderInfo
    const merchantBusinessId = toStringSafe((orderInfo as any).merchantId)
    if (merchantBusinessId) {
      (normalizedRecord as any).merchant_business_id = merchantBusinessId
    }
  }

  const rawChainIdentifier =
    toStringSafe(record.chain_id) ??
    (typeof record.chainId === 'number' && Number.isFinite(record.chainId)
      ? `${record.chainId}`
      : toStringSafe(record.chainId))
  if (rawChainIdentifier) {
    normalizedRecord.registry_chain_id = rawChainIdentifier
    normalizedRecord.registryChainId = rawChainIdentifier
  }

  const resolvedChainCode = toStringSafe(record.chain_code) ?? toStringSafe(record.chainCode)
  if (resolvedChainCode) {
    normalizedRecord.chain_code = resolvedChainCode
    normalizedRecord.chainCode = resolvedChainCode
  }

  if (record.chain_numeric_id != null) {
    normalizedRecord.chain_numeric_id = record.chain_numeric_id
    normalizedRecord.chainNumericId = record.chain_numeric_id
  }

  const resolvedChainDisplayName =
    toStringSafe(record.chain_display_name) ?? toStringSafe(record.chainDisplayName)
  if (resolvedChainDisplayName) {
    normalizedRecord.chain_display_name = resolvedChainDisplayName
    normalizedRecord.chainDisplayName = resolvedChainDisplayName
  }

  if ((record as any).registry) {
    (normalizedRecord as any).registry = (record as any).registry
  }

  if ((record as any).orderDetails) {
    const orderDetails = (record as any).orderDetails
    ;(normalizedRecord as any).orderDetails = orderDetails
    ;(normalizedRecord as any).order_details = orderDetails
  }

  return normalizedRecord
}

export const contractService = {
  async exportRegistryCsv(
    params?: ContractRegistryListParams,
    mode?: NetworkEnvironment
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await adminApiClient.get<Blob>('/admin/contract-registry/export', {
        params,
        responseType: 'blob',
        ...buildModeAwareConfig(mode),
      })

      return {
        blob: response.data,
        filename: extractDownloadFilename(
          response.headers['content-disposition'],
          `contract-registry-${new Date().toISOString().slice(0, 10)}.csv`
        ),
      }
    } catch (error) {
      throw new Error(await resolveBlobApiErrorMessage(error, 'Failed to export contract registry records'))
    }
  },

  async exportDeploymentCsv(
    params?: ContractDeploymentListParams,
    mode?: NetworkEnvironment
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await adminApiClient.get<Blob>('/admin/contract-deployment-records/export', {
        params,
        responseType: 'blob',
        ...buildModeAwareConfig(mode),
      })

      return {
        blob: response.data,
        filename: extractDownloadFilename(
          response.headers['content-disposition'],
          `contract-deployments-${new Date().toISOString().slice(0, 10)}.csv`
        ),
      }
    } catch (error) {
      throw new Error(await resolveBlobApiErrorMessage(error, 'Failed to export contract deployment records'))
    }
  },

  async triggerContractPreparation(params: {
    merchantId?: string
    orderIds?: string[]
    limit?: number
    force?: boolean
  }, mode?: NetworkEnvironment): Promise<ContractPreparationTriggerResult> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 50000)
    
    try {
      const response = await adminApiClient.post<ApiResponse<{
        orders_scanned: number
        orders_queued: number
        orders_skipped?: number
        message?: string
      }>>('/admin/contract-preparation/trigger', params, {
        ...buildModeAwareConfig(mode),
        signal: controller.signal,
        timeout: 50000
      })
      
      if (!response.data || !response.data.data) {
        throw new Error('Invalid response from contract preparation API')
      }

      const raw = response.data.data as Record<string, unknown>
      const batchResult = normalizeLifecycleBatchTriggerResult({
        total: raw.ordersScanned ?? raw.orders_scanned,
        queued: raw.ordersQueued ?? raw.orders_queued,
        alreadyQueued: raw.alreadyQueued ?? raw.already_queued,
        skipped: raw.ordersSkipped ?? raw.orders_skipped,
        failed: raw.failed,
        items: raw.items,
      })

      return {
        ordersScanned: batchResult.total,
        ordersQueued: batchResult.queued,
        ordersSkipped: batchResult.skipped,
        alreadyQueued: batchResult.alreadyQueued,
        failed: batchResult.failed,
        items: batchResult.items,
        message:
          typeof raw.message === 'string' && raw.message.trim().length > 0
            ? raw.message
            : undefined,
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
        throw new Error('Contract preparation timeout after 50 seconds')
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  },

  async triggerContractDeployment(params: {
    orderIds?: string[]
    chainId?: string
    limit?: number
  }, mode?: NetworkEnvironment): Promise<LifecycleBatchTriggerResult> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 45000)
    
    try {
      const response = await adminApiClient.post<ApiResponse<{
        total: number
        queued: number
        alreadyQueued: number
        skipped: number
        failed: number
        items?: unknown
      }>>('/admin/contract-deployment/trigger', params, {
        ...buildModeAwareConfig(mode),
        signal: controller.signal
      })
      
      const payload = response.data
      const data = payload?.data && typeof payload.data === 'object'
        ? payload.data
        : payload
      
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response from contract deployment API')
      }

      return normalizeLifecycleBatchTriggerResult(data)
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Contract deployment timeout after 45 seconds')
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  },

  async getContractRegistryRecords(
    params?: ContractRegistryListParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<ContractRegistryRecord>> {
    const searchParams = new URLSearchParams()
    
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.search) searchParams.append('search', params.search)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.chain_id) searchParams.append('chain_id', params.chain_id)
    if (params?.merchant_id) searchParams.append('merchant_id', params.merchant_id)
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder)

    const query = searchParams.toString()
    const url = query ? `/admin/contract-registry?${query}` : '/admin/contract-registry'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    const normalized = normalizePaginatedResponse<any>(response.data, {
      keys: ['records', 'registry', 'registries'],
      defaultPage: params?.page,
      defaultLimit: params?.limit,
    })
    const mapped = (normalized.data ?? []).map(mapContractRegistryRecord)
    return {
      ...normalized,
      data: mapped,
    } as PaginatedResponse<ContractRegistryRecord>
  },

  async getContractRegistryRecord(
    id: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<ContractRegistryRecord>> {
    const response = await adminApiClient.get<ApiResponse<ContractRegistryRecord>>(
      `/admin/contract-registry/${id}`,
      buildModeAwareConfig(mode)
    )
    const payload = response.data
    if (payload?.data) {
      const { deployment_history, ...record } = payload.data as any
      const normalizedRecord = mapContractRegistryRecord(record)
      if (deployment_history !== undefined) {
        (normalizedRecord as any).deployment_history = deployment_history
      }
      return {
        ...payload,
        data: normalizedRecord,
      }
    }
    return payload
  },

  async updateContractRegistryStatus(
    id: string,
    data: ContractRegistryStatusUpdate,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<ContractRegistryRecord>> {
    const response = await adminApiClient.put<ApiResponse<ContractRegistryRecord>>(
      `/admin/contract-registry/${id}/status`,
      data,
      buildModeAwareConfig(mode)
    )
    return response.data
  },

  async deployContractRegistry(
    id: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<{
    success: boolean
    message: string
    deployment_tx_hash?: string
    gas_used?: string
    cost_usd?: string
  }>> {
    const response = await adminApiClient.post<ApiResponse<{
      success: boolean
      message: string
      deployment_tx_hash?: string
      gas_used?: string
      cost_usd?: string
    }>>(`/admin/contract-registry/${id}/deploy`, {}, buildModeAwareConfig(mode))
    return response.data
  },

  async getContractDeploymentRecords(
    params?: ContractDeploymentListParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<ContractDeploymentRecord>> {
    const searchParams = new URLSearchParams()
    
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.search) searchParams.append('search', params.search)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.verification_status) searchParams.append('verification_status', params.verification_status)
    if (params?.chain_id) searchParams.append('chain_id', params.chain_id)
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder)

    const query = searchParams.toString()
    const url = query ? `/admin/contract-deployment-records?${query}` : '/admin/contract-deployment-records'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    const normalized = normalizePaginatedResponse<any>(response.data, {
      keys: ['deployments'],
      defaultPage: params?.page,
      defaultLimit: params?.limit,
    })
    const responsePayload: any = response.data
    const summary = responsePayload?.data?.summary ?? responsePayload?.summary
    const mapped = (normalized.data ?? []).map(mapContractDeploymentRecord)
    const baseResult: PaginatedResponse<ContractDeploymentRecord> = {
      ...normalized,
      data: mapped,
    }
    return summary
      ? ({ ...baseResult, summary } as PaginatedResponse<ContractDeploymentRecord>)
      : baseResult
  },

  async getContractDeploymentRecord(
    id: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<ContractDeploymentRecord>> {
    const response = await adminApiClient.get<ApiResponse<any>>(
      `/admin/contract-deployment-records/${id}`,
      buildModeAwareConfig(mode)
    )
    const payload = response.data
    if (payload?.data) {
      const rawData: any = payload.data
      const primary = rawData.deployment ?? rawData
      const normalizedRecord = mapContractDeploymentRecord(primary)
      if (rawData.registry) {
        (normalizedRecord as any).registry = rawData.registry
      }
      if (rawData.orderDetails) {
        (normalizedRecord as any).order_details = rawData.orderDetails
      }
      return {
        ...payload,
        data: normalizedRecord,
      }
    }
    return payload
  },

  async getContractInitializationStatus(
    orderBusinessId: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<ContractInitializationStatus>> {
    try {
      const response = await adminApiClient.get<ApiResponse<ContractInitializationStatus>>(
        `/admin/contracts/initialization/${orderBusinessId}`,
        buildModeAwareConfig(mode)
      )
      
      if (!response.data) {
        throw new Error('No response data received')
      }
      
      if (!response.data.data && response.data.success !== false) {
        console.warn('getContractInitializationStatus: Unexpected response structure', response.data)
      }
      
      return response.data
    } catch (error) {
      console.error('getContractInitializationStatus error:', error)
      throw error
    }
  },

  async getContractInitializationStatusByAddress(
    contractAddress: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<ContractInitializationStatus>> {
    const response = await adminApiClient.get<ApiResponse<ContractInitializationStatus>>(
      `/admin/contracts/initialization/address/${contractAddress}`,
      buildModeAwareConfig(mode)
    )
    return response.data
  },

  async getUninitializedContracts(
    params?: ContractInitializationQueryParams,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<ContractInitializationStatus[]>> {
    const searchParams = new URLSearchParams()
    
    if (params?.chainId) searchParams.append('chainId', params.chainId)
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.offset) searchParams.append('offset', params.offset.toString())

    const response = await adminApiClient.get<ApiResponse<ContractInitializationStatus[]>>(
      `/admin/contracts/initialization/uninitialized?${searchParams}`,
      buildModeAwareConfig(mode)
    )
    return response.data
  },

  async retryContractInitialization(
    orderBusinessId: string,
    data?: { force?: boolean },
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<{
    success: boolean
    message: string
    initialized?: boolean
    retry_queued?: boolean
  }>> {
    try {
      const response = await adminApiClient.post<ApiResponse<{
        success: boolean
        message: string
        initialized?: boolean
        retry_queued?: boolean
      }>>(
        `/admin/contracts/initialization/${orderBusinessId}/retry`,
        data || {},
        buildModeAwareConfig(mode)
      )
      
      if (!response.data) {
        throw new Error('No response data received')
      }
      
      console.debug('retryContractInitialization response:', response.data)
      
      return response.data
    } catch (error) {
      console.error('retryContractInitialization error:', error)
      throw error
    }
  },

  async queueContractVerification(
    deploymentId: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<{
    success: boolean
    message: string
    queued?: boolean
    verification_status?: string
  }>> {
    try {
      const response = await adminApiClient.post<ApiResponse<{
        success: boolean
        message: string
        queued?: boolean
        verification_status?: string
      }>>(`/admin/contracts/${deploymentId}/queue-verify`, {}, buildModeAwareConfig(mode))
      
      if (!response.data) {
        throw new Error('No response data received')
      }
      
      return response.data
    } catch (error) {
      console.error('queueContractVerification error:', error)
      throw error
    }
  },

  async getContractsNeedingVerification(params?: {
    chainId?: string
    limit?: number
    offset?: number
  }, mode?: NetworkEnvironment): Promise<ApiResponse<{
    contracts: Array<{
      deployment_id: string
      contract_address: string
      chain_id: string
      orderId: string
      verification_status: string
      deployment_status: string
      created_at: string
      updated_at: string
    }>
    pagination: {
      total: number
      page: number
      limit: number
      totalPages: number
    }
  }>> {
    const searchParams = new URLSearchParams()
    
    if (params?.chainId) searchParams.append('chainId', params.chainId)
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.offset) searchParams.append('offset', params.offset.toString())

    try {
      const response = await adminApiClient.get<ApiResponse<{
        contracts: Array<{
          deployment_id: string
          contract_address: string
          chain_id: string
          orderId: string
          verification_status: string
          deployment_status: string
          created_at: string
          updated_at: string
        }>
        pagination: {
          total: number
          page: number
          limit: number
          totalPages: number
        }
      }>>(`/admin/contracts/verification/pending?${searchParams}`, buildModeAwareConfig(mode))
      
      if (!response.data) {
        throw new Error('No response data received')
      }
      
      return response.data
    } catch (error) {
      console.error('getContractsNeedingVerification error:', error)
      throw error
    }
  },

  async queueBatchContractVerification(params: {
    chainId?: string
    verificationStatus?: string
    limit?: number
    orderIds?: string[]
  }, mode?: NetworkEnvironment): Promise<ApiResponse<{
    total: number
    queued: number
    skipped: number
    failed: number
    details?: Array<{
      deployment_id: string
      status: 'queued' | 'skipped' | 'failed'
      message?: string
      error?: string
    }>
  }>> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 60000)
    
    try {
      const response = await adminApiClient.post<ApiResponse<{
        total: number
        queued: number
        skipped: number
        failed: number
        details?: Array<{
          deployment_id: string
          status: 'queued' | 'skipped' | 'failed'
          message?: string
          error?: string
        }>
      }>>('/admin/contract-verification/trigger', params, {
        ...buildModeAwareConfig(mode),
        signal: controller.signal
      })
      
      if (!response.data) {
        throw new Error('No response data received')
      }
      
      return response.data
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Batch contract verification timeout after 60 seconds')
      }
      console.error('queueBatchContractVerification error:', error)
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  },

  async getContractVerificationStatus(params?: {
    chainId?: string
    verificationStatus?: string
    limit?: number
    offset?: number
  }, mode?: NetworkEnvironment): Promise<ApiResponse<{
    total: number
    pending: number
    confirmed: number
    failed: number
    contracts: Array<{
      deployment_id: string
      contract_address: string
      chain_id: string
      verification_status: string
      verification_notes?: string
      created_at: string
      updated_at: string
    }>
  }>> {
    const searchParams = new URLSearchParams()
    
    if (params?.chainId) searchParams.append('chainId', params.chainId)
    if (params?.verificationStatus) searchParams.append('verificationStatus', params.verificationStatus)
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.offset) searchParams.append('offset', params.offset.toString())

    try {
      const response = await adminApiClient.get<ApiResponse<{
        total: number
        pending: number
        confirmed: number
        failed: number
        contracts: Array<{
          deployment_id: string
          contract_address: string
          chain_id: string
          verification_status: string
          verification_notes?: string
          created_at: string
          updated_at: string
        }>
      }>>(`/admin/contracts/verification-status?${searchParams}`, buildModeAwareConfig(mode))
      
      if (!response.data) {
        throw new Error('No response data received')
      }
      
      return response.data
    } catch (error) {
      console.error('getContractVerificationStatus error:', error)
      throw error
    }
  },

  async triggerContractVerification(params: {
    chainId?: string
    limit?: number
    force?: boolean
    orderIds?: string[]
  }, mode?: NetworkEnvironment): Promise<LifecycleBatchTriggerResult> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 45000)
    
    try {
      const response = await adminApiClient.post<ApiResponse<{
        total: number
        queued: number
        alreadyQueued: number
        skipped: number
        failed: number
        items?: unknown
      }>>('/admin/contract-verification/trigger', params, {
        ...buildModeAwareConfig(mode),
        signal: controller.signal
      })
      
      if (!response.data || !response.data.data) {
        throw new Error('Invalid response from contract verification API')
      }
      
      return normalizeLifecycleBatchTriggerResult(response.data.data)
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Contract verification timeout after 45 seconds')
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  },

  async triggerContractInitialization(params: {
    chainId?: string
    limit?: number
    force?: boolean
    orderIds?: string[]
  }, mode?: NetworkEnvironment): Promise<LifecycleBatchTriggerResult> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 45000)

    try {
      const response = await adminApiClient.post<ApiResponse<{
        total: number
        queued: number
        alreadyQueued: number
        skipped: number
        failed: number
        items?: unknown
      }>>('/admin/contract-initialization/trigger', params, {
        ...buildModeAwareConfig(mode),
        signal: controller.signal
      })

      if (!response.data || !response.data.data) {
        throw new Error('Invalid response from contract initialization API')
      }

      return normalizeLifecycleBatchTriggerResult(response.data.data)
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Contract initialization timeout after 45 seconds')
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  },
}

const ADMIN_LIST_PAGE_LIMIT = 100

const collectAllPages = async <T>(
  label: string,
  fetchPage: (page: number, limit: number) => Promise<PaginatedResponse<T>>
): Promise<PaginatedResponse<T>> => {
  const firstPage = await fetchPage(1, ADMIN_LIST_PAGE_LIMIT)
  if (!firstPage.success) {
    throw new Error(firstPage.error || firstPage.message || `${label} request failed`)
  }

  const total = firstPage.pagination.total
  if (!Number.isSafeInteger(total) || total < 0) {
    throw new Error(`${label} pagination total is invalid`)
  }

  const records = [...(firstPage.data ?? [])]
  const pageCount = Math.ceil(total / ADMIN_LIST_PAGE_LIMIT)
  for (let page = 2; page <= pageCount; page += 1) {
    const nextPage = await fetchPage(page, ADMIN_LIST_PAGE_LIMIT)
    if (!nextPage.success) {
      throw new Error(nextPage.error || nextPage.message || `${label} page ${page} failed`)
    }
    if (nextPage.pagination.total !== total) {
      throw new Error(`${label} pagination total changed while loading`)
    }

    const pageRecords = nextPage.data ?? []
    const expectedCount = Math.min(
      ADMIN_LIST_PAGE_LIMIT,
      total - records.length
    )
    if (pageRecords.length !== expectedCount) {
      throw new Error(`${label} page ${page} returned an incomplete result set`)
    }
    records.push(...pageRecords)
  }

  if (records.length !== total) {
    throw new Error(`${label} result count does not match pagination total`)
  }

  return {
    ...firstPage,
    data: records,
    pagination: {
      page: 1,
      limit: ADMIN_LIST_PAGE_LIMIT,
      total,
      pages: pageCount,
      total_pages: pageCount,
    },
  }
}

export const getAllContractRegistryRecords = (
  params: Omit<ContractRegistryListParams, 'page' | 'limit'> = {},
  mode?: NetworkEnvironment
): Promise<PaginatedResponse<ContractRegistryRecord>> =>
  collectAllPages('Contract registry', (page, limit) =>
    contractService.getContractRegistryRecords({ ...params, page, limit }, mode)
  )

export const getAllContractDeploymentRecords = (
  params: Omit<ContractDeploymentListParams, 'page' | 'limit'> = {},
  mode?: NetworkEnvironment
): Promise<PaginatedResponse<ContractDeploymentRecord>> =>
  collectAllPages('Contract deployment records', (page, limit) =>
    contractService.getContractDeploymentRecords({ ...params, page, limit }, mode)
  )

export type {
  ContractRegistryListParams,
  ContractRegistryStatusUpdate,
  ContractDeploymentListParams,
  ContractInitializationQueryParams,
  ContractVerificationStatusParams,
  BatchContractVerificationParams,
} from '@/types'
